/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { EvaluationUpdateAction, UPDATE_EVALUATION } from '..';
import { DFSValueType } from '../../react/components/form/services/form';
import { Map } from 'immutable';
import { ActionType, getType } from 'typesafe-actions';
import { AppAction } from '..';
import { Evaluation } from '../../evaluation';
import { convertPointerToPathComponents } from '../../utils/runtime';
import { LOAD_EVALUATIONS, LOAD_EVALUATIONS_COMPLETED, LOAD_EVALUATION_COMPLETED } from './../actions';
import { acknowledgePublishSuccess } from './../interface/actions';
import { allDataUploaded, applyRemotePatches, clearActiveEval, clearEvaluation, createRemoteEval, deleteEvaluation, doPublishActions, fetchEvalPatchesActions, fetchEvaluation, loadEvaluations, pushPatchActions, saveCreateEvalRecord, setActiveEval, setEvalFilterAction, setSortDirection, setSortField, syncingEvals } from './actions';
const moment = require('moment');

export type EvalMap = Map<string, Evaluation>;

export interface EvalsState {
  items: EvalMap;
  activeEval?: string;
  /**
   * True if there is at least 1 in-flight request for an evaluation.
   */
  isFetching: boolean;
  /**
   * True while evals are being retrieved from indexedDB.
   */
  loading: boolean;
  limit: number;
  offset: number;
  sortField: string|undefined;
  sortDir: string|undefined;
  error?: Error;
  lastFetch: string;
  draftMode: boolean|null;
  type: string|undefined;
  total: number;
  facilityId: string|undefined;
  searchText: string;
  selectedEvalType: string;
  selectedDateRange: Date[]|null;
}

const updateEvalReducer: (state: EvalsState, action: EvaluationUpdateAction) => EvalsState = (state, action) => {
  if (!action.meta || !state.items.has(action.meta))
    // UPDATE_EVALUATION requires an evaluation ID be provided
    return state;

  let nextItemState: EvalMap;
  const targetPath = [action.meta, 'data', ...convertPointerToPathComponents(action.payload.path)]; // go from JSON Pointer to path component array
  switch (action.payload.op) {
    case 'add':
    case 'replace':
      if (/^-$/.test(targetPath[targetPath.length - 1])) {
        // Means add next array item
        const listPath = targetPath.slice(0, targetPath.length - 1);
        const list: DFSValueType[] = state.items.getIn(listPath) || [];
        nextItemState = state.items.setIn(listPath, [...list, action.payload.value]);
      } else nextItemState = state.items.setIn(targetPath, action.payload.value);
      break;
    case 'copy':
      nextItemState = state.items.setIn(
        targetPath,
        state.items.getIn(convertPointerToPathComponents(action.payload.from))
      );
      break;
    case 'remove':
      nextItemState = state.items.removeIn(targetPath);
      break;
    case 'move':
      const fromPath = state.items.getIn(convertPointerToPathComponents(action.payload.from));
      nextItemState = state.items.setIn(targetPath, fromPath);
      nextItemState = nextItemState.removeIn(fromPath);
      break;
    default:
      nextItemState = state.items;
  }

  nextItemState = nextItemState.setIn([action.meta, 'modified'], moment().toISOString());
  nextItemState = nextItemState.setIn([action.meta, 'published'], false);

  const syncStatus = nextItemState.getIn([action.meta, 'syncStatus']);

  if (syncStatus !== 'FAILED') {
    nextItemState = nextItemState.setIn([action.meta, 'syncStatus'], 'PENDING');
  }

  return {
    ...state,
    items: nextItemState,
  };
};

const batchUpdateEvalReducer: (state: EvalsState, action: ActionType<typeof applyRemotePatches>) => EvalsState = (
  state,
  action
) => {
  if (!action.meta)
    // UPDATE_EVALUATION requires an evaluation ID be provided
    return state;

  let nextState = state;
  action.payload.forEach((action) => {
    nextState = updateEvalReducer(nextState, action);
  });

  return nextState;
};

export const evalsReducer: (state: EvalsState | undefined, action: AppAction) => EvalsState = (
  state: EvalsState = {
    items: Map({}),
    loading: false,
    isFetching: false,
    limit: 25,
    offset: 0,
    sortField: 'DESC',
    sortDir: undefined,
    lastFetch: '',
    draftMode: true,
    type: undefined,
    total: 0,
    facilityId: undefined,
    searchText: '',
    selectedEvalType: '',
    selectedDateRange: null

  },
  action
) => {

  switch (action.type) {
    case LOAD_EVALUATIONS_COMPLETED:
      return {
        ...state,
        loading: false,
        items: Map({
          ...action.payload.evals,
        }),
        total: action.payload.total,
        newItems: false,
      };
    case getType(loadEvaluations):
      return {
        ...state,
        loading: true,
        draftMode: action.payload.drafts,
        limit: action.payload.limit,
        offset: action.payload.offset,
        type: action.payload.type,
        facilityId: action.payload.facilityId,
        sortField: action.payload.sortField,
        searchText: action.payload.searchText ?? '',
        selectedEvalType: action.payload.filters?.evalType ?? '',
        selectedDateRange: action.payload.filters?.dateRange ?? null
      };
    case LOAD_EVALUATIONS:
      return {
        ...state,
      };

    case getType(saveCreateEvalRecord):
      return {
        ...state,
        items: state.items.set(action.payload.id, action.payload.newEval),
      };
    case LOAD_EVALUATION_COMPLETED:
      return {
        ...state,
        items: state.items.set(action.payload.id, {
          ...action.payload,
          syncStatus: 'COMPLETE'
        }),
      };
    case getType(fetchEvaluation.success):
    case getType(doPublishActions.success):
      if (state.items.has(action.payload.id)) {
        return {
          ...state,
          items: state.items.set(action.payload.id, action.payload),
        };
      }

      return state;
    case getType(acknowledgePublishSuccess):
      return {
        ...state,
        items: state.items.remove(state.activeEval!),
      };
    case getType(pushPatchActions.success):
      return state.items.has(action.payload.id)
        ? {
            ...state,
            items: state.items.setIn([action.payload.id, 'lastSync'], action.payload.lastSync),
          }
        : { ...state, newItems: true };

    case getType(pushPatchActions.failure):
      if (state.items.has(action.payload.id)) {
        const items = state.items.setIn([action.payload.id, 'syncStatus'], 'FAILED');

        return {
          ...state,
          items
        }
      }

      return state;

    case getType(fetchEvalPatchesActions.success):
      if (state.items.has(action.payload.id)) {
        const items = state.items.setIn([action.payload.id, 'lastSync'], action.payload.lastSync);
        return {
          ...state,
          items
        };
      }

    return state;

    case getType(createRemoteEval.success): // only contributes new metadata to stored eval
      const { uuid, created, modified, lastSync } = action.payload;
      if (!state.items.has(action.payload.id)) return state;

      const nextItemsState = state.items
        .setIn([action.payload.id, 'uuid'], uuid)
        .setIn([action.payload.id, 'created'], created)
        .setIn([action.payload.id, 'modified'], modified)
        .setIn([action.payload.id, 'lastSync'], lastSync);
      return {
        ...state,
        items: nextItemsState,
      };
    case getType(setActiveEval):
      return {
        ...state,
        activeEval: action.payload.id,
      };
    case getType(clearActiveEval):
      return {
        ...state,
        activeEval: undefined,
      };
    case UPDATE_EVALUATION:
      return updateEvalReducer(state, action);
    case getType(applyRemotePatches):
      return batchUpdateEvalReducer(state, action);
    case getType(allDataUploaded):
      if (!state.items.has(action.meta)) {
        return state;
      }
      return {
        ...state,
        items: state.items.setIn([action.meta, 'syncStatus'], action.payload ? 'COMPLETE' : 'PENDING'),
      };
    case getType(setEvalFilterAction):
      return {
        ...state,
        draftMode: action.payload,
      };
    case getType(setSortField):
      return {
        ...state,
        sortField: action.payload,
      };
    case getType(setSortDirection):
      return {
        ...state,
        sortDirection: action.payload,
      };

    case getType(clearEvaluation.success):
    case getType(deleteEvaluation.success):
      return {
        ...state,
        items: state.items.remove(action.payload),
        total: state.total - 1,
      };

    case getType(syncingEvals):
      return {
        ...state,
        isFetching: action.payload,
      };

    default:
      return state;
  }
};
